import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import TutelaLogo from '../icons/TutelaLogo';
import IgnisIcon from '../icons/IgnisIcon';
import AuthNav from './AuthNav';

export default function Topbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isIgnisModule = location.pathname.startsWith('/ignis');
  // Dark "data-instrument" theme: the landing and the national risk map.
  // (Variable name kept as isLanding to avoid churn; it means "use dark chrome".)
  const DARK_ROUTES = ['/', '/sobre', '/pilotos', '/pilots', '/ignis', '/ignis/dashboard', '/ignis/map', '/ignis/mapa-nacional', '/ignis/terreno', '/ignis/briefing', '/ignis/nowcast', '/ignis/municipio', '/ignis/simulation', '/ignis/modelos', '/ignis/validacao', '/entrar', '/registar', '/conta'];
  // The per-concelho risk pages (/ignis/risco, /ignis/risco/:slug, district hubs) and the
  // municipal console (/ignis/municipio/:slug) use the dark "data-instrument" chrome too.
  // DARK_ROUTES is an exact match, so parameterised routes need the prefix test.
  const isLanding =
    DARK_ROUTES.includes(location.pathname) ||
    location.pathname.startsWith('/ignis/risco') ||
    location.pathname.startsWith('/ignis/municipio') ||
    location.pathname.startsWith('/limes') ||
    location.pathname.startsWith('/municipios');

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
      isActive
        ? isLanding
          ? 'bg-ink-800 text-mist'
          : 'bg-tutela-forest text-white shadow-warm'
        : isLanding
          ? 'text-mist-muted hover:text-mist hover:bg-ink-800'
          : 'text-tutela-charcoal hover:text-tutela-forest hover:bg-tutela-forest/5'
    }`;

  const ignisLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
      isActive
        ? 'bg-tutela-terracotta text-white'
        : 'text-tutela-slate hover:text-tutela-terracotta hover:bg-tutela-terracotta/5'
    }`;

  const ignisBtnClass = isLanding
    ? 'inline-flex items-center gap-2 rounded-lg bg-ember px-4 py-2 font-semibold text-ink-950 shadow-[0_0_24px_-10px_#F26A4B] transition-all duration-200 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember'
    : 'inline-flex items-center gap-2 rounded-xl bg-tutela-terracotta px-4 py-2 font-semibold text-white shadow-warm transition-all duration-200 hover:bg-tutela-terracotta/90 hover:shadow-warm-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tutela-terracotta';

  return (
    <nav
      className={`sticky top-0 z-50 backdrop-blur-lg border-b ${
        isLanding ? 'bg-ink-950/80 border-ink-800' : 'bg-white/95 shadow-sm border-gray-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-3 group">
            <TutelaLogo
              className="w-10 h-10 group-hover:scale-105 transition-transform"
              variant={isLanding ? 'light' : 'color'}
            />
            <div>
              <div
                className={`font-bold text-xl ${
                  isLanding ? 'font-display text-mist' : 'font-serif text-tutela-forest'
                }`}
              >
                Tutela
              </div>
              <div
                className={`text-xs -mt-0.5 ${
                  isLanding ? 'font-mono uppercase tracking-widest text-mist-faint' : 'text-tutela-slate'
                }`}
              >
                Stewards of Tomorrow
              </div>
            </div>
          </NavLink>

          {/* Desktop Navigation. Public IGNIS pills only (5); the grant-gated
              module links live in AuthNav's logged-in "A minha área" menu. */}
          <div className="hidden lg:flex items-center gap-2">
            <NavLink to="/ignis" end className={`ml-1 ${ignisBtnClass}`}>
              <IgnisIcon className="w-4 h-4" />
              IGNIS
            </NavLink>
            {!isIgnisModule && (
              <>
                <NavLink to="/limes" end className={linkClass}>
                  LIMES
                </NavLink>
                <NavLink to="/municipios" className={linkClass}>
                  Municípios
                </NavLink>
                <NavLink to="/sobre" className={linkClass}>
                  Sobre
                </NavLink>
              </>
            )}

            {isIgnisModule && (
              <div className="ml-4 pl-4 border-l border-gray-200 flex items-center gap-1">
                <NavLink to="/ignis/dashboard" className={ignisLinkClass}>
                  Dashboard
                </NavLink>
                <NavLink to="/ignis/mapa-nacional" className={ignisLinkClass}>
                  Nacional
                </NavLink>
                <NavLink to="/ignis/risco" className={ignisLinkClass}>
                  Concelhos
                </NavLink>
                <NavLink to="/ignis/validacao" className={ignisLinkClass}>
                  Validação
                </NavLink>
                <NavLink to="/ignis/modelos" className={ignisLinkClass}>
                  Modelos
                </NavLink>
              </div>
            )}
            <div
              className={`ml-2 pl-2 border-l flex items-center ${
                isLanding ? 'border-ink-700' : 'border-gray-200'
              }`}
            >
              <AuthNav isLanding={isLanding} />
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            className={`lg:hidden p-2 rounded-xl transition-colors ${
              isLanding ? 'hover:bg-ink-800' : 'hover:bg-gray-100'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className={`w-6 h-6 ${isLanding ? 'text-mist' : 'text-tutela-charcoal'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className={`lg:hidden py-4 border-t ${isLanding ? 'border-ink-800' : 'border-gray-100'}`}>
            <div className="flex flex-col gap-2">
              <NavLink
                to="/ignis"
                end
                onClick={() => setMobileMenuOpen(false)}
                className={ignisBtnClass}
              >
                <IgnisIcon className="w-4 h-4" />
                IGNIS
              </NavLink>
              {isIgnisModule && (
                <div className="ml-4 pl-4 border-l-2 border-tutela-terracotta/20 flex flex-col gap-1">
                  <NavLink
                    to="/ignis/dashboard"
                    className={ignisLinkClass}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </NavLink>
                  <NavLink
                    to="/ignis/mapa-nacional"
                    className={ignisLinkClass}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Mapa Nacional
                  </NavLink>
                  <NavLink
                    to="/ignis/risco"
                    className={ignisLinkClass}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Risco por concelho
                  </NavLink>
                  <NavLink
                    to="/ignis/validacao"
                    className={ignisLinkClass}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Validação
                  </NavLink>
                  <NavLink
                    to="/ignis/modelos"
                    className={ignisLinkClass}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Modelos
                  </NavLink>
                </div>
              )}
              <NavLink to="/limes" end className={linkClass} onClick={() => setMobileMenuOpen(false)}>
                LIMES
              </NavLink>
              <NavLink to="/municipios" className={linkClass} onClick={() => setMobileMenuOpen(false)}>
                Municípios
              </NavLink>
              <NavLink to="/sobre" className={linkClass} onClick={() => setMobileMenuOpen(false)}>
                Sobre
              </NavLink>
              <div
                className={`mt-2 pt-2 border-t ${isLanding ? 'border-ink-800' : 'border-gray-100'}`}
              >
                <AuthNav
                  isLanding={isLanding}
                  variant="mobile"
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
